import { defineStore } from 'pinia';
import { AssessmentPublisherService } from '@/services';
import { StoreInterface } from '@/types';
import { PublisherFilterInterface } from '@/services/AssessmentPublisherService';
import { PublisherInterface, PublisherSuccessResponse, ApiErrorResponse } from '@/types/AssessmentManagement';
import { useAssessmentStore } from '@/store';

interface PublisherStoreStateInterface extends StoreInterface {
  publishers: {
    loading: boolean;
    data: PublisherInterface[];
    total: number;
  };
}

export default defineStore('PublisherStore', {
  state: (): PublisherStoreStateInterface => ({
    publishers: {
      loading: false,
      data: [],
      total: 0,
    },
    message: '',
    error: false,
    loading: false,
  }),

  getters: {
    errorTitle: () => 'Publisher Error',
  },

  actions: {
    load() {
      this.error = false;
      this.message = '';
      this.loading = true;
    },

    catchError(e: ApiErrorResponse) {
      this.error = true;
      this.message = e.message;
      this.loading = false;
      throw e;
    },

    loaded(msg = '') {
      if (!this.error) {
        this.message = msg;
        this.loading = false;
      }
    },

    async getPublishers(filters?: PublisherFilterInterface) {
      this.publishers.loading = true;
      try {
        const response = await AssessmentPublisherService.getPublishers(filters);
        this.publishers.data = response.data.data;
        this.publishers.total = response.data.meta.total;
      } catch (error) {
        this.catchError(error as ApiErrorResponse);
      } finally {
        this.publishers.loading = false;
      }
    },

    async getPublisher(id: number) {
      this.load();
      try {
        const response = await AssessmentPublisherService.getPublisher(id);
        this.loaded();
        return response.data;
      } catch (error) {
        this.catchError(error as ApiErrorResponse);
      }
    },

    async createPublisher(title: string) {
      this.load();
      try {
        const response = await AssessmentPublisherService.createPublisher({ title });
        const { success } = response.data as PublisherSuccessResponse;
        this.loaded();
        const assessmentStore = useAssessmentStore();
        await assessmentStore.getOptions();
        return success;
      } catch (error) {
        this.catchError(error as ApiErrorResponse);
      }
    },

    async updatePublisher(id: number, title: string) {
      this.load();
      try {
        const response = await AssessmentPublisherService.updatePublisher(id, { title });
        const { success } = response.data as PublisherSuccessResponse;
        this.loaded();
        const assessmentStore = useAssessmentStore();
        await Promise.all([assessmentStore.getOptions(), assessmentStore.refreshCurrentAssessment()]);
        return success;
      } catch (error) {
        this.catchError(error as ApiErrorResponse);
      }
    },

    async deletePublisher(id: number) {
      this.load();
      try {
        const response = await AssessmentPublisherService.deletePublisher(id);
        const { success } = response.data as PublisherSuccessResponse;
        this.loaded();
        const assessmentStore = useAssessmentStore();
        await assessmentStore.getOptions();
        return success;
      } catch (error) {
        this.catchError(error as ApiErrorResponse);
      }
    },
  },
});
